import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router }              from '@angular/router';
import { Subscription }        from 'rxjs';
import { SessionService }      from 'src/app/core/services/session.service';
import { SignalRService }      from 'src/app/core/services/signalr.service';
import { SubscriptionService } from 'src/app/core/services/subscription.service';
import { AiInsightsService }   from 'src/app/features/services/ai-insights.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AppNotification }     from 'src/app/core/models/notification.model';

@Component({
  selector:    'app-dashboard-layout',
  templateUrl: './dashboard-layout.component.html',
  styleUrls:   ['./dashboard-layout.component.css']
})
export class DashboardLayoutComponent implements OnInit, OnDestroy {

  sidebarCollapsed = false;
  isMobile         = window.innerWidth < 1024;

  canAccessLiveMap    = false;
  canAccessReports    = false;
  canAccessAiInsights = false;

  aiUnreadCount      = 0;
  workersOnlineCount = 0;

  notifications: AppNotification[] = [];
  showDropdown   = false;

  user: { fullName: string; companyName?: string } | null = null;

  private subs: Subscription[] = [];

  constructor(
    private router:              Router,
    private sessionService:      SessionService,
    private signalRService:      SignalRService,
    private subscriptionService: SubscriptionService,
    private aiService:           AiInsightsService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.checkMobile();

    const session = this.sessionService.getSession();
    if (session) {
      this.user = {
        fullName:    session.fullName ?? 'Admin',
        companyName: session.companyName ?? undefined
      };
    }

    this.subs.push(
      this.subscriptionService.loadCurrent().subscribe(sub => {
        this.canAccessLiveMap    = sub.limits.hasLiveMap;
        this.canAccessReports    = sub.limits.hasReports;
        this.canAccessAiInsights = sub.limits.hasAiInsights;
      })
    );

    this.subs.push(
      this.aiService.unreadCount$.subscribe(n => {
        this.aiUnreadCount = n;
      })
    );

    this.aiService.loadUnreadCount().subscribe();

    this.subs.push(
      this.notificationService.notifications.subscribe(notifs => {
        this.notifications = notifs;
      })
    );

    this.signalRService.startConnection().then(() => {
      this.signalRService.on('WorkerOnline', () => {
        this.workersOnlineCount++;
      });

      this.signalRService.on('WorkerOffline', () => {
        this.workersOnlineCount = Math.max(0, this.workersOnlineCount - 1);
      });
    }).catch(err => {
      console.error('SignalR connection failed in layout:', err);
    });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkMobile();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.showDropdown) this.showDropdown = false;
  }

  private checkMobile(): void {
    this.isMobile = window.innerWidth < 1024;
    if (this.isMobile) this.sidebarCollapsed = true;
  }

  onNavClick(): void {
    if (this.isMobile) this.sidebarCollapsed = true;
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebar(): void {
    this.sidebarCollapsed = true;
  }

  // ── Notifications ─────────────────────────────────────

  get unreadCount(): number {
    return this.notifications.filter(n => !n.read).length;
  }

  toggleNotifications(event: MouseEvent): void {
    event.stopPropagation();
    this.showDropdown = !this.showDropdown;
  }

  clearNotifications(): void {
    this.notificationService.clearAll();
  }

  onNotificationClick(notif: AppNotification): void {
    this.notificationService.markRead(notif.id);
    this.showDropdown = false;
    if (notif.link) {
      this.router.navigateByUrl(notif.link);
    }
  }

  // ✅ NEW — dismiss a single notification without navigating or marking
  // it read first. stopPropagation() is essential here so this click
  // doesn't also fire the parent row's onNotificationClick (which would
  // navigate away unexpectedly).
  removeNotification(event: MouseEvent, id: string): void {
    event.stopPropagation();
    this.notificationService.removeOne(id);
  }

  getNotifIcon(type: string): string {
    switch (type) {
      case 'worker_online':       return '🟢';
      case 'worker_offline':      return '🔴';
      case 'work_log_submitted':  return '📝';
      case 'job_notification':    return '🗂️';
      default:                    return '🔔';
    }
  }

  getTimeAgo(timestamp: Date): string {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60)   return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)   return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)     return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }

  get userName(): string {
    return this.sessionService.getSession()?.fullName ?? 'Admin';
  }

  get companyName(): string {
    return this.sessionService.getSession()?.companyName ?? '';
  }

  get companyInitials(): string {
    return this.companyName
      .split(' ')
      .slice(0, 2)
      .map(w => w.charAt(0).toUpperCase())
      .join('');
  }

  logout(): void {
    this.sessionService.clearSession();
    this.router.navigate(['/auth/login']);
  }
}